import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles, AlertTriangle, Lightbulb, Loader2 } from 'lucide-react'
import { askDataPilot, type DPNamedSnapshot, type DPResponse, type DPAnomaly } from '../../api/datapilot'

const SEVERITY_DOT: Record<DPAnomaly['severity'], string> = {
  info: 'text-sky-500',
  warning: 'text-amber-500',
  critical: 'text-red-500',
}

interface Props {
  metrics: DPNamedSnapshot[]
  period: string
}

/**
 * Auto-generated "DataPilot Insights" banner shown at the top of the dashboard.
 * Summarises the period across the supplied metric snapshots.
 */
export const DashboardNarrative: React.FC<Props> = ({ metrics, period }) => {
  const { t, i18n } = useTranslation()
  const [resp, setResp] = useState<DPResponse | null>(null)
  const [loading, setLoading] = useState(false)

  // Re-summarise when the underlying metrics meaningfully change.
  const sig = JSON.stringify(metrics.map((m) => [m.metric_id, m.snapshot.value, m.snapshot.change_pct]))

  useEffect(() => {
    if (metrics.length === 0) return
    let cancelled = false
    setLoading(true)
    askDataPilot({ kind: 'summary', period, locale: i18n.language, metrics })
      .then((r) => {
        if (!cancelled) setResp(r)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, period, i18n.language])

  if (metrics.length === 0) return null

  return (
    <div className="rounded-lg border border-accent/30 bg-gradient-to-br from-accent/5 to-transparent p-5">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent text-white">
          <Sparkles className="h-4 w-4" />
        </span>
        <h2 className="text-sm font-bold text-slate-900 dark:text-white">
          {t('datapilot.narrative', 'DataPilot Insights')}
        </h2>
        {resp?.source && (
          <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-500 dark:bg-slate-700">
            {resp.source === 'demo' ? t('datapilot.demoBadge', 'demo') : t('datapilot.liveBadge', 'AI')}
          </span>
        )}
      </div>

      {loading && !resp ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('datapilot.loading', 'DataPilot is thinking…')}
        </div>
      ) : (
        resp && (
          <div className="space-y-3">
            <p className="text-sm text-slate-700 dark:text-slate-300">{resp.answer}</p>

            {resp.insights && resp.insights.length > 0 && (
              <ul className="grid gap-1.5 sm:grid-cols-2">
                {resp.insights.map((ins, i) => (
                  <li key={i} className="flex gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <span>{ins}</span>
                  </li>
                ))}
              </ul>
            )}

            {resp.anomalies && resp.anomalies.length > 0 && (
              <div className="space-y-1.5">
                {resp.anomalies.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                    <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${SEVERITY_DOT[a.severity]}`} />
                    <span>{a.message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      )}
    </div>
  )
}
